import type { ReactNode } from "react";
import { useMemo } from "react";
import { AcceleratorBatchSource } from "../../models/AcceleratorBatchSource";
import Icon from "../Icon";
import { LockIndicator } from "../locking/LockIndicator";
import { useLoginDetails } from "../login/UserDetailsProvider";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTBatchId } from "../tooltips/ConfigurationTooltips";
import "../../styles/documentSelector/batchGroup.css";

export { BatchGroup };

type Props = {
  batchUuid: string;
  batchInstanceId: string;
  batchName: string;
  source: AcceleratorBatchSource;
  lockedBy?: string;
  status: "" | "Reprocessing" | "Queued";
  /**
   * Checkbox for selecting batches. If undefined, no checkbox is shown
   */
  checkbox: ReactNode;
  collapsed: boolean;
  collapse: () => void;
};

const BatchGroup = (props: Props) => {
  const userDetails = useLoginDetails();

  const batchLabel = useMemo(() => {
    return props.batchInstanceId + " : " + props.batchName;
  }, [props.batchInstanceId, props.batchUuid]);

  return (
    <div className={"batchGroup"} data-batch={props.batchUuid}>
      {props.checkbox}

      <span className={"line"} />

      <span data-bi-id>
        <BatchLabel label={batchLabel} source={props.source} />

        {props.lockedBy && (
          <LockIndicator
            currentUser={userDetails.username ?? ""}
            lockedBy={props.lockedBy}
          />
        )}
      </span>
      <span className={"line"} />
      {props.status !== "" && (
        <>
          <span data-status>{props.status}</span>
          <span className={"line"} />
        </>
      )}

      {props.checkbox != undefined && (
        <span data-collapse onClick={props.collapse}>
          <Icon
            altText={"Collapse/Expand"}
            icon={props.collapsed ? "chevron-left" : "chevron-down"}
          />
        </span>
      )}
    </div>
  );
};

const BATCH_COLOURS: { [x in AcceleratorBatchSource]: string } = {
  EXTERNAL: "cyan",
  TRANSACT: "darkgreen",
  HELIOS: "Helios",
};
// const FONT_COLOURS: { [x in AcceleratorBatchSource]: string } = {
//     'EXTERNAL': 'white',
//     'TRANSACT': 'white',
//     'HELIOS': 'white',
// }
type BatchLabelProps = {
  label: string;
  source: AcceleratorBatchSource;
};
const BatchLabel = (props: BatchLabelProps) => {
  const labelColour = BATCH_COLOURS[props.source];
  return (
    <AdvancedTooltip short tooltipText={TTBatchId}>
      {(tProps) => (
        <span
          {...tProps}
          className={`circle ${labelColour}`}
          style={{
            marginRight: "0.5rem",
            // dynamically resize
            width: `${props.label.length}ch`,
          }}
        >
          {props.label}
        </span>
      )}
    </AdvancedTooltip>
  );
};
